import { FlexBox, IconButton, Text } from '@dixa/lib-shared-habla';
import { type ChatStatus, type FileUIPart } from 'ai';
import classnames from 'classnames';
import {
  ChangeEvent,
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { type SidekickAgentMode } from '../../../../utils/sdk/structuredResponse';
import {
  ATTACHMENT_ACCEPT,
  MAX_ATTACHMENTS,
  toAttachment,
} from '../../utils/sidekickAttachments';

import { SidekickAttachmentChip } from './components/sidekickAttachmentChip/sidekickAttachmentChip';
import { SidekickModeSelector } from './components/sidekickModeSelector/sidekickModeSelector';
import styles from './sidekickComposer.module.scss';
import { pickAttachments } from './sidekickComposer.utils';

type SidekickComposerProps = {
  status: ChatStatus;
  agentMode: SidekickAgentMode;
  onAgentModeChange: (agentMode: SidekickAgentMode) => void;
  onSend: (text: string, files: FileUIPart[]) => void;
  onStop: () => void;
};

type FormValues = {
  prompt: string;
};

type SentMessage = {
  prompt: string;
  attachments: FileUIPart[];
};

const PLACEHOLDERS = {
  do: 'Describe what you want to do…',
  plan: 'Describe what you want to plan…',
} as const satisfies Record<SidekickAgentMode, string>;

const NOTHING_SENT: SentMessage = { prompt: '', attachments: [] };
const READ_ERROR = 'The file couldn’t be read. Try attaching it again.';

export const SidekickComposer = ({
  status,
  agentMode,
  onAgentModeChange,
  onSend,
  onStop,
}: SidekickComposerProps): JSX.Element => {
  const { register, handleSubmit, getValues, setValue, setFocus, watch } =
    useForm<FormValues>({
      mode: 'all',
      defaultValues: {
        prompt: '',
      },
    });
  const [attachments, setAttachments] = useState<FileUIPart[]>([]);
  const [attachmentError, setAttachmentError] = useState<string>();

  const prompt = watch('prompt');
  const isGenerating = status === 'submitted' || status === 'streaming';
  const hasError = status === 'error';

  const lastSentRef = useRef(NOTHING_SENT);
  const modeSelectorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Only a failed send from this composer is restored.
  useEffect(() => {
    if (status === 'ready') lastSentRef.current = NOTHING_SENT;
    if (status === 'error' && !getValues('prompt')) {
      setValue('prompt', lastSentRef.current.prompt);
      setAttachments(lastSentRef.current.attachments);
    }
  }, [status, getValues, setValue]);

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleValidSubmit({ prompt });
    }
  };

  const handleValidSubmit: SubmitHandler<FormValues> = (values) => {
    const { prompt } = values;

    const trimmed = prompt.trim();
    if (!trimmed || isGenerating) return;

    lastSentRef.current = { prompt: trimmed, attachments };
    onSend(trimmed, attachments);
    setValue('prompt', '');
    setAttachments([]);
    setAttachmentError(undefined);
  };

  const handleFilesChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';

    const { picked, error } = pickAttachments(files, attachments.length);
    setAttachmentError(error);
    if (picked.length === 0) return;

    try {
      const added = await Promise.all(
        picked.map(({ file, mediaType }) => toAttachment(file, mediaType)),
      );
      setAttachments((current) =>
        [...current, ...added].slice(0, MAX_ATTACHMENTS),
      );
    } catch {
      setAttachmentError(READ_ERROR);
    }
  };

  const handleRemoveAttachment = (removed: FileUIPart) => {
    setAttachments((current) =>
      current.filter((attachment) => attachment !== removed),
    );
    setAttachmentError(undefined);
  };

  const handleComposerClick = (event: MouseEvent<HTMLFormElement>) => {
    const isInModeSelector =
      event.target instanceof Node &&
      modeSelectorRef.current?.contains(event.target);
    if (isInModeSelector) return;
    setFocus('prompt');
  };

  return (
    <div className={styles.root}>
      <form
        className={classnames(styles.composer, { [styles.error]: hasError })}
        onSubmit={handleSubmit(handleValidSubmit)}
        onClick={handleComposerClick}
      >
        {attachments.length > 0 && (
          <FlexBox flexWrap="wrap" gap="200">
            {attachments.map((attachment, index) => (
              <SidekickAttachmentChip
                key={`${attachment.filename}-${index}`}
                attachment={attachment}
                onRemove={() => handleRemoveAttachment(attachment)}
              />
            ))}
          </FlexBox>
        )}
        <textarea
          {...register('prompt')}
          className={styles.input}
          placeholder={
            isGenerating ? 'Sidekick is responding…' : PLACEHOLDERS[agentMode]
          }
          disabled={isGenerating}
          onKeyDown={handleKeyDown}
          dir="auto"
        />
        <FlexBox justifyContent="space-between" alignItems="center">
          <FlexBox alignItems="center" gap="200">
            <div ref={modeSelectorRef}>
              <SidekickModeSelector
                agentMode={agentMode}
                isDisabled={isGenerating}
                onAgentModeChange={onAgentModeChange}
              />
            </div>
            <IconButton
              icon="paperclip"
              variant="tertiary"
              type="button"
              aria-label="Add attachment"
              disabled={isGenerating || attachments.length >= MAX_ATTACHMENTS}
              onClick={() => fileInputRef.current?.click()}
            />
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ATTACHMENT_ACCEPT}
              className={styles.fileInput}
              onChange={(event) => void handleFilesChange(event)}
            />
          </FlexBox>
          {isGenerating ? (
            <IconButton
              icon="stop"
              variant="tertiary"
              aria-label="Stop"
              className={styles.action}
              onClick={onStop}
            />
          ) : (
            <IconButton
              icon="paper-plane"
              variant="tertiary"
              type="submit"
              aria-label="Send"
              className={styles.action}
              disabled={prompt.trim().length === 0}
            />
          )}
        </FlexBox>
      </form>
      {attachmentError && (
        <Text.Paragraph variant="4" color="error">
          {attachmentError}
        </Text.Paragraph>
      )}
      {hasError && (
        <Text.Paragraph variant="4" color="error">
          Message not sent. Check your connection and try again.
        </Text.Paragraph>
      )}
    </div>
  );
};
